/**
 * @file Documents.jsx
 * @description Handles document upload and verification during onboarding
 *
 * @section Responsibilities
 * - Manage document upload process
 * - Handle document verification
 * - Track document status
 * - Validate document requirements
 * - Provide document preview
 * - Manage document storage
 *
 * @section Dependencies
 * - DocumentService: File handling and storage
 * - VerificationService: Document validation
 * - StorageService: File storage management
 * - PreviewService: Document preview generation
 *
 * @section Integration Points
 * - Document API: File upload and retrieval
 * - Verification System: Document validation
 * - Storage System: File storage
 * - Onboarding Flow: Progress tracking
 *
 * @section State Management
 * - documents: Uploaded documents
 * - verificationStatus: Document validation status
 * - uploadProgress: File upload status
 * - requirements: Document requirements
 *
 * @section Events
 * - onDocumentUpload: File upload handling
 * - onVerification: Document validation
 * - onPreview: Document preview
 * - onError: Error handling
 *
 * @section Props
 * @prop {Array} requiredDocuments - List of required documents
 * @prop {Function} onComplete - Completion callback
 * @prop {Object} initialDocuments - Previously uploaded documents
 * @prop {Boolean} allowReupload - Controls document reupload
 */

import axios from "axios";
import React, { useState, useEffect } from "react";
import "./../../assets/fonts.css";
import "./../../styles/global.css";
import "./../../styles/strategy-selection.css";
import DgButton from "../../components/DgButton";
import { useNavigate } from "react-router-dom";
import BallsBackground from "../../components/BallsBackground";
import FileDropZone from "../../components/forms/FileDropZone";

function useElementSize(element) {
  const [elementSize, setElementSize] = useState({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    function handleResize() {
      setElementSize({
        width: element.current ? element.current.offsetWidth : 0,
        height: element.current ? element.current.offsetHeight : 0,
      });
    }

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return elementSize;
}

function Documents() {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const user = JSON.parse(localStorage.session);

  const [data, setData] = useState({
    strategy: "Bull",
  });

  const updateStrategy = (stg) => {
    setData({ strategy: stg });
  };

  const updateUser = () => {
    setLoading(true);

    axios
      .put(`${process.env.REACT_APP_BASE_URL}/user`, {
        PK: user.PK,
        SK: user.SK,
        ...data,
      })
      .then((res) => {
        navigate("/dashboard");
      });
  };

  const [idFile, setIdFile] = useState([]);
  const [imageUrl, setImageUrl] = useState(null);

  const handleDropId = (acceptedFiles) => {
    const uploadedFile = acceptedFiles[0];
    setIdFile(uploadedFile);

    const reader = new FileReader();
    reader.onload = () => {
      setImageUrl(reader.result);
    };
    reader.readAsDataURL(uploadedFile);
  };

  const [signatureFile, setSignatureFile] = useState([]);
  const [signatureImageUrl, setSignatureImageUrl] = useState(null);

  const handleDropSignature = (acceptedFiles) => {
    const uploadedFile = acceptedFiles[0];
    setSignatureFile(uploadedFile);

    const reader = new FileReader();
    reader.onload = () => {
      setSignatureImageUrl(reader.result);
    };
    reader.readAsDataURL(uploadedFile);
  };

  return (
    <div className="strategy-selection">
      <BallsBackground>
        <div className="content container shadow1">
          <div className="progress-bar step2">
            <div className="progress"></div>
            <div className="step active">
              <div className="title">
                <div className="number">
                  <span>1</span>
                </div>
                <p>Your profile</p>
              </div>
            </div>
            <div className="step active">
              <div className="title">
                <div className="number">
                  <span>2</span>
                </div>
                <p>Agreements</p>
              </div>
            </div>
            <div className="step">
              <div className="title">
                <div className="number">
                  <span>3</span>
                </div>
                <p>Documents</p>
              </div>
            </div>
          </div>
          <div className="inner-content">
            <div className="heading">
              <p className="subtitle">Step 3</p>
              <h1 className="title">Documents</h1>
              <p className="description"></p>
            </div>
            <div className="flex dropzone-all">
              <div className="filesArea">
                <h2>ID</h2>
                <FileDropZone onDrop={handleDropId} />
                <div>
                  <h3>Uploaded File</h3>
                  {idFile && <p>{idFile.name}</p>}
                  {imageUrl && (
                    <img
                      src={imageUrl}
                      alt="Uploaded Preview"
                      style={{ maxWidth: "100%", height: "auto" }}
                    />
                  )}
                </div>
              </div>
              <div className="filesArea">
                <h2>Signature</h2>
                <FileDropZone onDrop={handleDropSignature} />
                <div>
                  <h3>Uploaded File</h3>
                  {signatureFile && <p>{signatureFile.name}</p>}
                  {signatureImageUrl && (
                    <img
                      src={signatureImageUrl}
                      alt="Uploaded Preview"
                      style={{ maxWidth: "100%", height: "auto" }}
                    />
                  )}
                </div>
              </div>
            </div>

            <DgButton size={"large"} category={"primary"}>
              Save
            </DgButton>
          </div>
        </div>
      </BallsBackground>
    </div>
  );
}

export default Documents;
